import { config } from "./config";
import { currentUserId } from "./global";

/**
 * Facebook login by full-page redirect, not by SDK popup.
 *
 * The JS SDK was tried first and could not be made to work here: FB.login
 * opened no window and never called back, which is what a blocked popup, a
 * content blocker, or Chrome's third-party cookie rules all look like from
 * inside the page. The redirect flow has none of those failure modes — the
 * browser simply navigates — and it works the same on mobile.
 *
 * Facebook returns the short-lived token in the URL fragment, which is exactly
 * what the SDK used to hand us, so nothing downstream changes.
 */
const STATE_KEY = "fbOauthState";

const graphVersion = () => config.facebookGraphApiVersion || "v22.0";

function randomState() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);

  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

/** Where Facebook sends the user back to — must match the app's whitelist. */
export function redirectUri() {
  return `${window.location.origin}${window.location.pathname}`;
}

/**
 * Leaves the portal for Facebook's consent dialog. Nothing after this runs:
 * the page is replaced.
 */
export function startFacebookLogin(scope) {
  if (!config.facebookAppId) {
    throw new Error("No Facebook app id is configured for this portal");
  }

  const state = randomState();

  // The reply is bound to the account that started the login. Without the id, a
  // login begun by one account and finished after signing in as another saved
  // the first account's Facebook token onto the second account.
  sessionStorage.setItem(
    STATE_KEY,
    JSON.stringify({ state, userId: currentUserId() })
  );

  const parameters = new URLSearchParams({
    client_id: config.facebookAppId,
    redirect_uri: redirectUri(),
    state,
    response_type: "token",
    scope,
    // So we can tell a partial grant from a full one when we come back.
    return_scopes: "true",
    auth_type: "rerequest",
  });

  window.location.assign(
    `https://www.facebook.com/${graphVersion()}/dialog/oauth?${parameters}`
  );
}

/** The pending state, as {state, userId}; null when there is none. */
function readExpectedState() {
  const raw = sessionStorage.getItem(STATE_KEY);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);

    return typeof parsed === "object" && parsed !== null ? parsed : null;
  } catch (error) {
    // A value written before the state carried a user id.
    return { state: raw, userId: currentUserId() };
  }
}

/**
 * Reads whatever Facebook left in the URL after sending the user back.
 *
 * Returns null when this is an ordinary visit. The fragment is cleared either
 * way, so a reload cannot replay a spent token or repeat an error.
 */
export function readFacebookRedirect() {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const query = new URLSearchParams(window.location.search);

  const accessToken = hash.get("access_token");
  const error = hash.get("error") || query.get("error");

  if (!accessToken && !error) {
    return null;
  }

  const expected = readExpectedState();
  const state = hash.get("state") || query.get("state");

  sessionStorage.removeItem(STATE_KEY);
  window.history.replaceState(null, "", window.location.pathname);

  if (error) {
    const description =
      hash.get("error_description") ||
      query.get("error_description") ||
      "The Facebook login was cancelled.";

    return { error: description.replace(/\+/g, " ") };
  }

  // A reply that does not carry back the state we sent is not ours.
  if (!expected || state !== expected.state) {
    return { error: "That sign-in reply did not match this browser session." };
  }

  // Nor is one that belongs to an account that is no longer signed in here.
  if (expected.userId !== currentUserId()) {
    return {
      error:
        "That sign-in was started by a different account. Sign in again and " +
        "connect from this account.",
    };
  }

  return {
    accessToken,
    grantedScopes: (hash.get("granted_scopes") || "").split(",").filter(Boolean),
  };
}

/** The profile the store shows, read straight from the Graph API. */
export async function fetchFacebookProfile(accessToken) {
  const response = await fetch(
    `https://graph.facebook.com/${graphVersion()}/me?fields=id,name,email,picture&access_token=${encodeURIComponent(
      accessToken
    )}`
  );
  const data = await response.json();

  if (data.error) {
    throw new Error(data.error.message);
  }

  return data;
}
